import {
  Avatar,
  Box,
  Card,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/joy";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import {
  useGenralTeamSettingsQuery,
  useTeamUpdateMutation,
} from "../../../../__generated__/types-and-hooks";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import FileUploadButton from "../../../../components/FileUploadButton";
import { ReactNode } from "react";
import { useTranslation } from "../../../../i18n";
import DOMPurify from "dompurify";
import Email from "@mui/icons-material/EmailOutlined";
import WhatsApp from "@mui/icons-material/WhatsApp";
import Phone from "@mui/icons-material/LocalPhone";
import Message from "@mui/icons-material/Message";
import TelegramIcon from "@mui/icons-material/Telegram";
import LanguageIcon from "@mui/icons-material/Language"; // for website
import BusinessIcon from "@mui/icons-material/Business"; // for LinkedIn
import InstagramIcon from "@mui/icons-material/Instagram"; // optional
import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X"; // for Twitter (now X)

// Map of contact method keys to corresponding icons
const iconMap: Record<string, ReactNode> = {
  phone: <Phone fontSize="small" />,
  email: <Email fontSize="small" />,
  whatsapp: <WhatsApp fontSize="small" />,
  telegram: <TelegramIcon fontSize="small" />,
  linkedin: <BusinessIcon fontSize="small" />,
  website: <LanguageIcon fontSize="small" />,
  twitter: <XIcon fontSize="small" />,
  facebook: <FacebookIcon fontSize="small" />,
  instagram: <InstagramIcon fontSize="small" />,
  other: <Message fontSize="small" />,
};

export default function General() {
  const navigate = useNavigate();
  const t = useTranslation();
  const { teamSlug } = useParams();
  const [teamUpdateMutation] = useTeamUpdateMutation();
  const genralTeamSettingsQuery = useGenralTeamSettingsQuery({
    variables: { slug: teamSlug! },
  });

  const team = genralTeamSettingsQuery.data?.team_by_slug;
  if (!team) return null;

  return (
    <Box sx={{ padding: 2, width: "100%", margin: "0 auto" }}>
      <Checkbox
        label={t("Show in Search & Network")}
        size="md"
        variant="outlined"
        checked={team.public}
        onChange={(e) => {
          teamUpdateMutation({
            variables: {
              team: {
                id: team.id,
                public: e.target.checked,
              },
            },
            onCompleted() {
              genralTeamSettingsQuery.refetch();
            },
          });
        }}
      />
      <Card
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: 0,
          marginBottom: 3,
          boxShadow: "0px 0px 3px #000000",
          background: "var(surface)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: 220,
            width: "100%",
          }}
        >
          {team?.background && (
            <img
              src={team?.background}
              alt="Background"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 8,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }}
            />
          )}
          <Box sx={{ position: "absolute", top: 20, right: 20 }}>
            <FileUploadButton
              isPublic
              onSuccess={async ([file]) => {
                teamUpdateMutation({
                  variables: {
                    team: {
                      id: team.id,
                      background: "https://app.qcsku.com/" + (file as any).key,
                      contacts: team.contacts,
                    },
                  },
                  onCompleted() {
                    genralTeamSettingsQuery.refetch();
                  },
                });
              }}
            />
          </Box>
        </Box>
        <Avatar
          sx={{
            position: "absolute",
            width: 200,
            height: 200,
            marginBottom: 2,
            boxShadow: "lg",
            marginTop: 0,
            top: 50,
            left: 20,
            border: 2,
            borderColor: "var(--joy-palette-background-level1)",
          }}
          src={team?.picture ?? "/images/qcsku_light.jpg"}
          alt={t("Team Logo")}
        />
        <Box sx={{ position: "absolute", top: 210, left: 100 }}>
          <FileUploadButton
            isPublic
            onSuccess={async ([file]) => {
              teamUpdateMutation({
                variables: {
                  team: {
                    id: team.id,
                    picture: "https://app.qcsku.com/" + (file as any).key,
                    contacts: team.contacts,
                  },
                },
                onCompleted() {
                  genralTeamSettingsQuery.refetch();
                },
              });
            }}
          />
        </Box>
        <Box
          gap={1}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          <Box
            gap={1}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              width: "100%",
            }}
          >
            <Stack gap={1} sx={{ flex: 1 }}>
              <Typography
                level="h1"
                fontWeight="bold"
                endDecorator={
                  <IconButton
                    sx={{ ml: 2 }}
                    variant="soft"
                    color="primary"
                    onClick={() => navigate("./edit-name")}
                  >
                    <CreateOutlinedIcon />
                  </IconButton>
                }
              >
                {team?.name}
              </Typography>
              <Typography level="body-md">{team.description}</Typography>
            </Stack>
            <Stack gap={1}>
              <Typography
                level="body-md"
                sx={{ alignItems: "baseline" }}
                endDecorator={
                  <IconButton
                    sx={{ ml: 2 }}
                    variant="soft"
                    color="primary"
                    onClick={() => navigate("./edit-contacts")}
                  >
                    <CreateOutlinedIcon />
                  </IconButton>
                }
              >
                {t("Contacts")}
              </Typography>
              {team.contacts.map((method, index) => (
                <Chip
                  key={index}
                  variant="soft"
                  sx={{ borderRadius: 4 }}
                  startDecorator={
                    iconMap[method.key] ?? <Message style={{ fontSize: 14 }} />
                  }
                >
                  {method.value}
                </Chip>
              ))}
            </Stack>
          </Box>
          {/* About Section */}
          <Stack gap={1} sx={{ alignItems: "left", width: "100%" }}>
            <Typography
              level="h2"
              sx={{ fontSize: "lg" }}
              endDecorator={
                <IconButton
                  sx={{ ml: 2 }}
                  variant="soft"
                  color="primary"
                  onClick={() => navigate("./about")}
                >
                  <CreateOutlinedIcon />
                </IconButton>
              }
            >
              {t("About your team")}
            </Typography>
            {team && team.about && (
              <Typography>
                <div
                  className="quill-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(team.about),
                  }}
                />
              </Typography>
            )}
          </Stack>
        </Box>
      </Card>
      <Outlet />
    </Box>
  );
}
